import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../../../redux/store";
import { setOcclusalAssessment } from "../../../../../redux/diagnosestate/diagnoseAction";

const defaultOcclusal = {
  incisorRelationship: "",
  canineRelationshipRight: "",
  canineRelationshipLeft: "",
  molarRelationshipRight: "",
  molarRelationshipLeft: "",
  anteriorGuidance: "",
  lateralGuidanceRight: "",
  lateralGuidanceLeft: "",
};

const inputClass =
  "w-full px-3 py-2 rounded-lg border border-gray-600 dark:border-slate-600 focus:outline-none transition-all resize-none text-xs bg-slate-50 dark:bg-slate-700 text-slate-800 dark:text-slate-200 placeholder-slate-500 dark:placeholder-slate-400";
const labelClass =
  "block text-xs font-medium text-slate-700 dark:text-slate-400 mb-1";

function OcclusalAssessment() {
  const [isOpen, setIsOpen] = useState(false);
  const dispatch = useDispatch();
  const occlusalAssessment =
    useSelector((state: RootState) => state.diagnose.occlusalAssessment) ||
    defaultOcclusal;

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    dispatch(setOcclusalAssessment({ [name]: value }));
  };

  return (
    <div className="bg-white dark:bg-slate-800 rounded-[8px] shadow-lg shadow-slate-200/50 dark:shadow-slate-900/50 border border-slate-100 dark:border-slate-700 overflow-hidden">
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className="w-full px-4 py-2.5 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between gap-2 text-left hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors"
      >
        <h4 className="text-sm font-bold text-slate-800 dark:text-slate-100">
          Occlusal assessment
        </h4>
        <svg
          className={`w-4 h-4 text-slate-500 dark:text-slate-400 shrink-0 transition-transform duration-300 ease-in-out ${isOpen ? "rotate-180" : ""}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      <div
        className={`grid transition-[grid-template-rows] duration-300 ease-in-out ${
          isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
        }`}
      >
        <div className="min-h-0 overflow-hidden">
      <div className="p-4 grid grid-cols-2 gap-4">
        {/* Static */}
        <section className="space-y-3">
          <h5 className="text-xs font-semibold text-slate-600 dark:text-slate-300 mb-3">
            Static
          </h5>
          <div className="space-y-3 pl-2">
            <div>
              <label className={labelClass}>1. Incisor relationship</label>
              <textarea
                name="incisorRelationship"
                rows={2}
                value={occlusalAssessment.incisorRelationship ?? ""}
                onChange={handleChange}
                placeholder="Enter incisor relationship..."
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>2. Canine relationship</label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <span className="text-xs text-slate-500 dark:text-slate-400 mb-1 block">
                    Right
                  </span>
                  <textarea
                    name="canineRelationshipRight"
                    rows={2}
                    value={occlusalAssessment.canineRelationshipRight ?? ""}
                    onChange={handleChange}
                    placeholder="Right..."
                    className={inputClass}
                  />
                </div>
                <div>
                  <span className="text-xs text-slate-500 dark:text-slate-400 mb-1 block">
                    Left
                  </span>
                  <textarea
                    name="canineRelationshipLeft"
                    rows={2}
                    value={occlusalAssessment.canineRelationshipLeft ?? ""}
                    onChange={handleChange}
                    placeholder="Left..."
                    className={inputClass}
                  />
                </div>
              </div>
            </div>
            <div>
              <label className={labelClass}>3. Molar relationship</label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <span className="text-xs text-slate-500 dark:text-slate-400 mb-1 block">
                    Right
                  </span>
                  <textarea
                    name="molarRelationshipRight"
                    rows={2}
                    value={occlusalAssessment.molarRelationshipRight ?? ""}
                    onChange={handleChange}
                    placeholder="Right..."
                    className={inputClass}
                  />
                </div>
                <div>
                  <span className="text-xs text-slate-500 dark:text-slate-400 mb-1 block">
                    Left
                  </span>
                  <textarea
                    name="molarRelationshipLeft"
                    rows={2}
                    value={occlusalAssessment.molarRelationshipLeft ?? ""}
                    onChange={handleChange}
                    placeholder="Left..."
                    className={inputClass}
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Dynamic */}
        <section className="space-y-3 border-l border-slate-200 dark:border-slate-600 pl-4">
          <h5 className="text-xs font-semibold text-slate-600 dark:text-slate-300 mb-3">
            Dynamic
          </h5>
          <div className="space-y-3 pl-2">
            <div>
              <label className={labelClass}>1. Anterior guidance</label>
              <textarea
                name="anteriorGuidance"
                rows={2}
                value={occlusalAssessment.anteriorGuidance ?? ""}
                onChange={handleChange}
                placeholder="Enter anterior guidance..."
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>2. Lateral guidance</label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <span className="text-xs text-slate-500 dark:text-slate-400 mb-1 block">
                    Right
                  </span>
                  <textarea
                    name="lateralGuidanceRight"
                    rows={2}
                    value={occlusalAssessment.lateralGuidanceRight ?? ""}
                    onChange={handleChange}
                    placeholder="Right..."
                    className={inputClass}
                  />
                </div>
                <div>
                  <span className="text-xs text-slate-500 dark:text-slate-400 mb-1 block">
                    Left
                  </span>
                  <textarea
                    name="lateralGuidanceLeft"
                    rows={2}
                    value={occlusalAssessment.lateralGuidanceLeft ?? ""}
                    onChange={handleChange}
                    placeholder="Left..."
                    className={inputClass}
                  />
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
        </div>
      </div>
    </div>
  );
}

export default OcclusalAssessment;
